import { useMemo, useCallback, useEffect, useRef } from 'react'

import { flattenToolPages, useListToolsQuery } from '@/services/queries/tools'
import { ToolName } from '@/components/editor/tiptap/ToolName'

export const useToolNameExtension = ({
  documentVersionId,
  toolBackgroundColor,
}: {
  documentVersionId?: string
  toolBackgroundColor?: string
} = {}) => {
  const { data: toolsData } = useListToolsQuery({
    limit: 100,
    offset: 0,
    /**
     * Tool mark resolution uses document-version scope so stale tool ids
     * can be rendered with fallback styling/content.
     */
    documentVersionId:
      typeof documentVersionId === 'string' && documentVersionId.trim() !== ''
        ? documentVersionId
        : undefined,
  })
  const tools = useMemo(() => flattenToolPages(toolsData), [toolsData])
  const toolNameByIdRef = useRef<Map<string, string>>(new Map())

  const toolNameById = useMemo(() => {
    return new Map(
      tools
        .filter((tool) => Boolean(tool?.id && tool?.name))
        .map((tool) => [tool.id, tool.name]),
    )
  }, [tools])

  useEffect(() => {
    toolNameByIdRef.current = toolNameById
  }, [toolNameById])

  const resolveToolNameById = useCallback((toolId: string) => {
    return toolNameByIdRef.current.get(toolId)
  }, [])

  const toolNameExtension = useMemo(() => {
    return ToolName.configure({
      resolveToolNameById,
      toolBackgroundColor,
    })
  }, [resolveToolNameById, toolBackgroundColor])

  return {
    toolNameExtension,
    toolNameById,
  }
}
